import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'
import { useSocket } from '../context/SocketContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTs  = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const money  = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'
const moneyN = (n) => Number(n || 0).toFixed(2)

// ── Tier meta ─────────────────────────────────────────────────────────────────
const TIER_META = {
  bronze:   { label: 'Bronze',   icon: '🥉', color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  silver:   { label: 'Silver',   icon: '🥈', color: '#374151', bg: '#f3f4f6', border: '#d1d5db' },
  gold:     { label: 'Gold',     icon: '🥇', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  platinum: { label: 'Platinum', icon: '💎', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
}

function TierBadge({ tier }) {
  const m = TIER_META[tier] || TIER_META.bronze
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
      {m.icon} {m.label}
    </span>
  )
}

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportEarningsCSV(orders, tips, dateFrom, dateTo, commPct = 0, tierInfo = null, ipayPct = 0) {
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const hasComm = commPct > 0
  const basePct = tierInfo?.platform_pct_base     ? (tierInfo.platform_pct_base     * 100).toFixed(0) + '%' : ''
  const effPct  = tierInfo?.platform_pct_effective ? (tierInfo.platform_pct_effective * 100).toFixed(0) + '%' : ''
  const headers = [
    'Order ID', 'Buyer', 'Phone', 'Date', 'Order Total (RWF)',
    'Shop Subtotal (RWF)', 'iPay Fee (RWF)',
    `Platform Fee${effPct ? ` (${effPct} effective)` : ''} (RWF)`,
    'Product Payout (RWF)',
    'Self-Delivery Gross (RWF)',
    ...(hasComm ? [`Delivery Commission (${commPct}%) (RWF)`] : []),
    'Self-Delivery Net (RWF)',
    'Payment Method',
    ...(tierInfo?.tier ? ['Tier', 'Platform % Base', 'Platform % Effective', 'Tier Saving (RWF)'] : []),
  ]
  const lines = orders.map(o => [
    o.order_id,
    o.buyer_name  || '',
    o.buyer_phone || '',
    fmtTs(o.created_at),
    moneyN(o.total_amount),
    moneyN(o.shop_subtotal),
    moneyN(o.ipay_fee),
    moneyN(o.platform_fee),
    moneyN(o.product_payout),
    moneyN(o.delivery_gross),
    ...(hasComm ? [moneyN(o.delivery_commission)] : []),
    moneyN(o.delivery_payout),
    o.payment_method || '',
    ...(tierInfo?.tier ? [
      tierInfo.tier,
      basePct,
      effPct,
      moneyN(Number(o.shop_subtotal || 0) * (tierInfo.tier_discount || 0)),
    ] : []),
  ].map(escape).join(','))
  const iPct = (ipayPct * 100).toFixed(0)
  const tipHeaders = ['Tip ID', 'Buyer', 'Phone', 'Date', 'Emoji',
    `Gross Tip (RWF)`, `iPay Fee ${iPct}% (RWF)`, 'Platform Fee (RWF)', `Net Tip Payout (RWF)`, 'Method']
  const tipLines = tips.map(tip => {
    const gross   = Number(tip.gross_amount || tip.amount || 0)
    const platFee = Number(tip.platform_fee || 0)
    const ipayFee = Number(tip.ipay_fee     || +(gross * ipayPct).toFixed(2))
    const net     = Number(tip.net_amount   || gross - ipayFee - platFee)
    return [
      tip.tip_id, tip.buyer_name || '', tip.buyer_phone || '',
      fmtTs(tip.created_at), tip.emoji || '',
      moneyN(gross), moneyN(ipayFee), moneyN(platFee), moneyN(net), tip.payment_method || '',
    ].map(escape).join(',')
  })

  const csvParts = [headers.join(','), ...lines]
  if (tips.length > 0) {
    csvParts.push('', `"=== TIPS (${tips.length}) ==="`, '',
      tipHeaders.join(','), ...tipLines,
      '', `"Tips Gross Total",${escape(moneyN(tips.reduce((s,t)=>s+Number(t.gross_amount||t.amount||0),0)))}`,
      `"iPay Fee Total",${escape(moneyN(tips.reduce((s,t)=>s+Number(t.ipay_fee||0),0)))}`,
      `"Tips Platform Fee Total",${escape(moneyN(tips.reduce((s,t)=>s+Number(t.platform_fee||0),0)))}`,
      `"Tips Net Payout",${escape(moneyN(tips.reduce((s,t)=>s+Number(t.net_amount||0),0)))}`,
    )
  }

  const csv  = csvParts.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-earnings-${dateFrom}_${dateTo}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// ── Sub-components ────────────────────────────────────────────────────────────
function SummaryCard({ label, value, sub, accent, bold }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: bold ? 22 : 20, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Earnings() {
  const now        = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

  const [orders,      setOrders]      = useState([])
  const [tips,        setTips]        = useState([])
  const [totals,      setTotals]      = useState(null)
  const [tierInfo,    setTierInfo]    = useState(null)
  const [ipayPct,     setIpayPct]     = useState(0)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [dateFrom,    setDateFrom]    = useState(monthStart)
  const [dateTo,      setDateTo]      = useState(monthEnd)
  const [tab,          setTab]          = useState('orders')
  const [exporting,    setExporting]    = useState(false)
  const [payRequests,  setPayRequests]  = useState([])
  const [payLoading,   setPayLoading]   = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const [payMomo,      setPayMomo]      = useState('')
  const [payNote,      setPayNote]      = useState('')
  const [paySubmit,    setPaySubmit]    = useState(false)
  const [payError,     setPayError]     = useState('')
  const [commPct,     setCommPct]     = useState(0)
  const [hasNewData,  setHasNewData]  = useState(false)
  const fetchIdRef = useRef(0)

  const { on } = useSocket()

  const fetchEarnings = useCallback(async () => {
    const id = ++fetchIdRef.current
    setLoading(true); setError(''); setHasNewData(false)
    try {
      const res = await api.get(`/orders/earnings-detail?date_from=${dateFrom}&date_to=${dateTo}`)
      if (id !== fetchIdRef.current) return
      setOrders(res.data.orders  || [])
      setTips(res.data.tips      || [])
      setTotals(res.data.totals  || null)
      setCommPct(res.data.driver_comm_pct || 0)
      setIpayPct(res.data.ipay_pct || 0)
      setTierInfo({
        tier:                   res.data.tier                   ?? 'bronze',
        platform_pct_base:      res.data.platform_pct_base      ?? 0,
        platform_pct_effective: res.data.platform_pct_effective ?? 0,
        tier_discount:          res.data.tier_discount          ?? 0,
        is_overridden:          res.data.is_overridden          ?? false,
      })
    } catch {
      if (id !== fetchIdRef.current) return
      setError('Failed to load earnings.')
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }, [dateFrom, dateTo])

  useEffect(() => { fetchEarnings() }, [fetchEarnings])

  useEffect(() => {
    if (tab !== 'payouts') return
    setPayLoading(true)
    api.get('/payments/seller/payout-requests')
      .then(res => setPayRequests(res.data.requests || []))
      .catch(() => setPayRequests([]))
      .finally(() => setPayLoading(false))
  }, [tab])

  const handleRequestPayout = async () => {
    setPayError('')
    setPaySubmit(true)
    try {
      const res = await api.post('/payments/seller/request-payout', {
        amount:      unsettledTotal || null,
        period_from: dateFrom,
        period_to:   dateTo,
        momo_number: payMomo.trim() || null,
        note:        payNote.trim() || null,
      })
      setPayRequests(prev => [res.data.request, ...prev])
      setShowPayModal(false)
      setPayMomo(''); setPayNote('')
      setTab('payouts')
    } catch (err) {
      setPayError(err.response?.data?.error || 'Request failed. Please try again.')
    } finally { setPaySubmit(false) }
  }

  // Flag new data when a payment notification arrives
  useEffect(() => {
    const unsub = on('notification', (n) => {
      if (n.type === 'new_order') setHasNewData(true)
    })
    return unsub
  }, [on])

  const t = totals || {}

  const selfDeliveryOrders = orders.filter(o => o.is_self_delivery || Number(o.delivery_gross) > 0)
  const periodLabel        = `${fmt(dateFrom)} – ${fmt(dateTo)}`

  // Summary values straight from totals (same shape as seller-payout-report)
  const sumProduct    = Number(t.product_payout      || 0)
  const sumDelivGross = Number(t.delivery_gross       || 0)
  const sumDelivComm  = Number(t.delivery_commission  || 0)
  const sumDelivNet   = Number(t.delivery_payout      || 0)
  const sumTips       = Number(t.tips_total           || 0)
  const sumTotal      = Number(t.total_payout         || 0)

  // Settled vs unsettled split — backend now provides these directly
  const unsettledTotal = Number(t.unsettled_total ?? (
    orders.filter(o => !o.settled_batch_id).reduce((s, o) => s + Number(o.product_payout || 0) + Number(o.delivery_payout || 0), 0) +
    tips.filter(tp => !tp.settled_batch_id).reduce((s, tp) => s + Number(tp.net_amount || tp.amount || 0), 0)
  ))
  const settledTotal = Number(t.settled_total ?? (
    orders.filter(o => !!o.settled_batch_id).reduce((s, o) => s + Number(o.product_payout || 0) + Number(o.delivery_payout || 0), 0) +
    tips.filter(tp => !!tp.settled_batch_id).reduce((s, tp) => s + Number(tp.net_amount || tp.amount || 0), 0)
  ))
  const canRequestPayout = unsettledTotal > 0

  // Page-level footer aggregates
  const pageProduct  = orders.reduce((s, o) => s + Number(o.product_payout     || 0), 0)
  const pageDelivNet = orders.reduce((s, o) => s + Number(o.delivery_payout     || 0), 0)
  const pageDelivComm= orders.reduce((s, o) => s + Number(o.delivery_commission || 0), 0)
  const pageTips     = tips.reduce((s, tip) => s + Number(tip.amount            || 0), 0)

  const handleExportCSV = () => {
    setExporting(true)
    try { exportEarningsCSV(orders, tips, dateFrom, dateTo, commPct, tierInfo, ipayPct) }
    finally { setExporting(false) }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Earnings</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Product revenue and self-delivery fee payouts.</div>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exporting || orders.length === 0}
          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: exporting ? '#bfdbfe' : '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 8px rgba(29,78,216,0.2)' }}>
          {exporting ? '⏳ Exporting…' : '⬇ Export CSV'}
        </button>
      </div>

      {/* ── Filters row — identical to SellerPayment ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>

        {/* Month shortcuts */}
        {[0, 1, 2].map(monthsAgo => {
          const d    = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1)
          const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
          const to   = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10)
          const lbl  = monthsAgo === 0 ? 'This month' : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })
          const active = dateFrom === from && dateTo === to
          return (
            <button key={monthsAgo} onClick={() => { setDateFrom(from); setDateTo(to) }}
              style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {lbl}
            </button>
          )
        })}
      </div>

      {/* ── Period label ── */}
      <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 16 }}>
        Report period: <b style={{ color: '#111317' }}>{periodLabel}</b> · {orders.length.toLocaleString()} orders
      </div>

      {/* ── Summary cards — identical to SellerPayment ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <SummaryCard
          label="Total Product Payout"
          value={money(sumProduct)}
          accent="#15803d" bold
          sub={`gross sales minus iPay & platform fees · ${orders.length} orders`}
        />
        {sumDelivGross > 0 && (
          <SummaryCard
            label="Self-Delivery Net"
            value={money(sumDelivNet)}
            accent="#1d4ed8"
            sub={commPct > 0 ? `after ${commPct}% platform cut · gross ${money(sumDelivGross)}` : 'delivery fee you handled'}
          />
        )}
        {commPct > 0 && sumDelivComm > 0 && (
          <SummaryCard
            label={`Delivery Commission (${commPct}%)`}
            value={money(sumDelivComm)}
            accent="#e8440a"
            sub="platform cut from your delivery fees"
          />
        )}
        {sumTips > 0 && (
          <SummaryCard
            label="Tips Net Payout"
            value={money(sumTips)}
            accent="#7c3aed"
            sub={ipayPct > 0
              ? `${Number(t.tips_count || tips.length)} tips · gross ${money(t.tips_gross_total || sumTips)} − iPay ${money(t.tips_ipay_total || 0)} − plat ${money(t.tips_platform_total || 0)}`
              : `${Number(t.tips_count || tips.length).toLocaleString()} tips`}
          />
        )}
        <SummaryCard
          label="Grand Total Payout"
          value={money(sumTotal)}
          accent="#111317" bold
          sub={`product + delivery + tips · ${orders.length.toLocaleString()} orders`}
        />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* New data banner */}
      {hasNewData && !loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 16px', marginBottom: 14, fontSize: 13 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', flexShrink: 0, boxShadow: '0 0 0 3px rgba(22,163,74,0.2)' }} />
          <span style={{ fontWeight: 600, color: '#15803d', flex: 1 }}>New payment received — earnings may have updated.</span>
          <button onClick={fetchEarnings} style={{ height: 30, padding: '0 14px', borderRadius: 7, border: 'none', background: '#15803d', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Refresh
          </button>
        </div>
      )}

      {/* ── Payout summary strip — identical to SellerDrawer header strip ── */}
      {!loading && totals && (
        <div style={{ padding: '14px 18px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
            Payout Summary · {periodLabel} · {orders.length} orders{tips.length > 0 ? ` · ${tips.length} tips` : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {[
              { lbl: 'Product Payout',    val: money(t.product_payout),                                          color: '#15803d'  },
              { lbl: 'Self-Delivery Net', val: selfDeliveryOrders.length > 0 ? money(t.delivery_payout) : '—',  color: '#1d4ed8'  },
              { lbl: `Tips Net (${tips.length})`, val: t.tips_total > 0 ? money(t.tips_total) : '—',          color: '#7c3aed'  },
              { lbl: 'Total Payout',      val: money(t.total_payout),                                            color: '#111317', bold: true },
            ].map(({ lbl, val, color, bold }) => (
              <div key={lbl} style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 7, padding: '8px 10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 8, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontSize: bold ? 13 : 12, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
              </div>
            ))}
          </div>
          {t.delivery_gross > 0 && commPct > 0 && (
            <div style={{ marginTop: 8, fontSize: 10, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
              Self-delivery: {money(t.delivery_gross)} gross − {commPct}% commission = {money(t.delivery_payout)} net
            </div>
          )}
          {tierInfo && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: 8 }}>
              <TierBadge tier={tierInfo.tier} />
              {tierInfo.tier_discount > 0 ? (
                <span style={{ fontSize: 10, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>
                  Platform fee: <b>{(tierInfo.platform_pct_effective * 100).toFixed(0)}%</b>
                  <span style={{ color: '#9a9a94' }}> (base {(tierInfo.platform_pct_base * 100).toFixed(0)}% − {(tierInfo.tier_discount * 100).toFixed(0)}pp tier discount)</span>
                  {' '}· Saved this period: <b style={{ color: '#15803d' }}>{money(Number(t.gross_subtotal || 0) * tierInfo.tier_discount)}</b>
                </span>
              ) : (
                <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                  Platform fee: {(tierInfo.platform_pct_base * 100).toFixed(0)}% — reach Silver to earn a discount
                </span>
              )}
              {tierInfo.is_overridden && (
                <span style={{ fontSize: 8, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace" }}>ADMIN SET</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab bar — identical to SellerDrawer ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 14, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        {[
          { id: 'orders',  label: `Orders (${orders.length})` },
          { id: 'tips',    label: `Tips (${tips.length})`     },
          { id: 'payouts', label: `Payouts (${payRequests.length})` },
        ].map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            style={{ flex: 1, padding: '11px 0', border: 'none', background: tab === tb.id ? '#111317' : 'transparent', color: tab === tb.id ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.12s' }}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* ── Orders table — pixel-perfect copy of SellerDrawer orders tab ── */}
      {tab === 'orders' && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>

          <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1fr 1fr 1fr 0.7fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['#', 'Buyer', 'Order Total', 'Product Payout', 'Delivery', 'Payout'].map(h => (
              <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading…</div>
          ) : orders.length === 0 ? (
            <div style={{ padding: '56px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>💰</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>No paid orders yet</div>
              <div style={{ fontSize: 13, color: '#9a9a94' }}>Earnings will appear here once buyers place paid orders.</div>
            </div>
          ) : (
            <>
              {orders.map((o, i) => {
                const isPaid = !!o.settled_batch_id
                return (
                <div key={o.order_id || i}
                  style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1fr 1fr 1fr 0.7fr', padding: '11px 22px', borderBottom: i < orders.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center', background: isPaid ? '#f0fdf4' : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = isPaid ? '#dcfce7' : '#fafaf8'}
                  onMouseLeave={e => e.currentTarget.style.background = isPaid ? '#f0fdf4' : 'transparent'}>

                  {/* # */}
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{o.order_id}</div>

                  {/* Buyer */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.buyer_name || 'Anonymous'}</div>
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTs(o.created_at)}</div>
                  </div>

                  {/* Order total */}
                  <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{moneyN(o.total_amount)}</div>

                  {/* Product payout */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(o.product_payout)}</div>
                    {Number(o.ipay_fee) > 0 && (
                      <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                        sub {moneyN(o.shop_subtotal)} − iPay {moneyN(o.ipay_fee)} − platform {moneyN(o.platform_fee)}
                        {tierInfo?.tier_discount > 0 && (
                          <span style={{ color: '#15803d' }}> ({(tierInfo.platform_pct_effective * 100).toFixed(0)}% fee)</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Delivery */}
                  <div>
                    {(o.is_self_delivery || Number(o.delivery_gross) > 0) ? (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#1d4ed8' }}>{moneyN(o.delivery_payout)}</div>
                        {Number(o.delivery_gross) > 0 && commPct > 0 && (
                          <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                            {moneyN(o.delivery_gross)} −{commPct}%
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 10, color: '#d1d5db', fontStyle: 'italic' }}>—</div>
                    )}
                  </div>

                  {/* Payout status */}
                  <div>
                    {isPaid ? (
                      <span style={{ display: 'inline-block', background: '#dcfce7', color: '#15803d', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", border: '1px solid #bbf7d0' }}>
                        PAID ✓
                      </span>
                    ) : (
                      <span style={{ display: 'inline-block', background: '#fffbeb', color: '#b45309', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", border: '1px solid #fde68a' }}>
                        PENDING
                      </span>
                    )}
                  </div>
                </div>
                )
              })}

              {/* Footer totals row */}
              {totals && (
                <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1fr 1fr 1fr 0.7fr', padding: '12px 22px', borderTop: '2px solid #e9e9e4', background: '#f0fdf4', alignItems: 'center' }}>
                  <div />
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Total ({orders.length})</div>
                  <div />
                  <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(pageProduct)}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#1d4ed8' }}>{moneyN(pageDelivNet)}</div>
                    {pageDelivComm > 0 && (
                      <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>comm {moneyN(pageDelivComm)}</div>
                    )}
                  </div>
                  <div />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Tips table — pixel-perfect copy of SellerDrawer tips tab ── */}
      {tab === 'tips' && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          {tips.length === 0 ? (
            <div style={{ padding: '56px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>💜</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>No tips yet</div>
              <div style={{ fontSize: 13, color: '#9a9a94' }}>Tips from buyers appear here.</div>
            </div>
          ) : (
            <>
              {ipayPct > 0 && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderBottom: '1px solid #e9e9e4', padding: '8px 22px', fontSize: 11, color: '#92400e', fontFamily: "'JetBrains Mono',monospace" }}>
                  ℹ️ Tips shown as gross (buyer paid) → iPay fee ({(ipayPct*100).toFixed(0)}%) → platform tip commission deducted → net payout you receive
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.2fr 0.6fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr 0.6fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                {['', 'Tipper', 'Date', 'Gross Tip', `iPay ${(ipayPct*100).toFixed(0)}%`, 'Platform', 'Net Payout', 'Method', 'Payout'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                ))}
              </div>

              {tips.map((tip, i) => {
                const gross   = Number(tip.gross_amount || tip.amount || 0)
                const platFee = Number(tip.platform_fee || 0)
                const ipayFee = Number(tip.ipay_fee     || +(gross * ipayPct).toFixed(2))
                const net     = Number(tip.net_amount   || gross - ipayFee - platFee)
                const isPaid  = !!tip.settled_batch_id
                return (
                  <div key={tip.tip_id || i}
                    style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.2fr 0.6fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr 0.6fr', padding: '11px 22px', borderBottom: i < tips.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center', background: isPaid ? '#f0fdf4' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = isPaid ? '#dcfce7' : '#fafaf8'}
                    onMouseLeave={e => e.currentTarget.style.background = isPaid ? '#f0fdf4' : 'transparent'}>
                    <div style={{ fontSize: 20, lineHeight: 1 }}>{tip.emoji || '💜'}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tip.buyer_name || 'Anonymous'}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{tip.buyer_phone || '—'}</div>
                    </div>
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTs(tip.created_at)}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(gross)}</div>
                    <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>− {moneyN(ipayFee)}</div>
                    <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>− {moneyN(platFee)}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{moneyN(net)}</div>
                    <div>
                      <span style={{ display: 'inline-block', background: '#f3f4f6', color: '#374151', fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'JetBrains Mono',monospace" }}>
                        {tip.payment_method || '—'}
                      </span>
                    </div>
                    <div>
                      {isPaid ? (
                        <span style={{ display: 'inline-block', background: '#dcfce7', color: '#15803d', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", border: '1px solid #bbf7d0' }}>
                          PAID ✓
                        </span>
                      ) : (
                        <span style={{ display: 'inline-block', background: '#faf5ff', color: '#7c3aed', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", border: '1px solid #e9d5ff' }}>
                          PENDING
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Tips total footer */}
              <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.2fr 0.6fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr 0.6fr', padding: '12px 22px', borderTop: '2px solid #e9e9e4', background: '#faf5ff', alignItems: 'center' }}>
                <div />
                <div style={{ fontSize: 11, fontWeight: 800, color: '#7c3aed' }}>Total ({tips.length} tips)</div>
                <div />
                <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                  {moneyN(tips.reduce((s,t)=>s+Number(t.gross_amount||t.amount||0),0))}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>
                  − {moneyN(tips.reduce((s,t)=>s+Number(t.ipay_fee||0),0))}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>
                  − {moneyN(tips.reduce((s,t)=>s+Number(t.platform_fee||0),0))}
                </div>
                <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>
                  {moneyN(tips.reduce((s,t)=>s+Number(t.net_amount||0),0))}
                </div>
                <div />
                <div />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Payouts tab ── */}
      {tab === 'payouts' && (
        <div>
          {/* Settled vs Unsettled breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {/* Already paid */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 18px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>
                ✓ Already Paid Out
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#15803d', letterSpacing: '-0.5px', fontFamily: "'JetBrains Mono',monospace" }}>
                {money(settledTotal)}
              </div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>
                {orders.filter(o => !!o.settled_batch_id).length} orders disbursed by admin
              </div>
            </div>

            {/* Pending / requestable */}
            <div style={{ background: canRequestPayout ? '#fffbeb' : '#f9f9f7', border: `1px solid ${canRequestPayout ? '#fde68a' : '#e9e9e4'}`, borderRadius: 10, padding: '16px 18px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: canRequestPayout ? '#b45309' : '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>
                  {canRequestPayout ? '⏳ Pending Payout' : '— Nothing Pending'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: canRequestPayout ? '#b45309' : '#9a9a94', letterSpacing: '-0.5px', fontFamily: "'JetBrains Mono',monospace" }}>
                  {money(unsettledTotal)}
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>
                  {orders.filter(o => !o.settled_batch_id).length} orders awaiting disbursement
                  {sumTips > 0 ? ` + ${money(sumTips)} tips` : ''}
                </div>
              </div>
              {canRequestPayout && (
                <button onClick={() => { setShowPayModal(true); setPayError('') }}
                  style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: '#b45309', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2 }}>
                  💸 Request
                </button>
              )}
            </div>
          </div>

          {!canRequestPayout && orders.length > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
              ✓ All earnings for this period have been paid out. Select a different date range to see pending orders.
            </div>
          )}

          {/* Payout request history */}
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>Payout Request History</div>
              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{payRequests.length} request{payRequests.length !== 1 ? 's' : ''}</div>
            </div>
            {payLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading…</div>
            ) : payRequests.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>💸</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 4 }}>No payout requests yet</div>
                <div style={{ fontSize: 13, color: '#9a9a94' }}>Once you request a payout it will appear here.</div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                  {['Requested', 'Period', 'Amount', 'MoMo', 'Status'].map(h => (
                    <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                  ))}
                </div>
                {payRequests.map((r, i) => {
                  const ST = {
                    pending:    { bg: '#fffbeb', color: '#b45309', label: 'Pending'    },
                    approved:   { bg: '#f0fdf4', color: '#15803d', label: 'Approved'   },
                    paid:       { bg: '#f0fdf4', color: '#15803d', label: 'Paid ✓'     },
                    rejected:   { bg: '#fef2f2', color: '#b91c1c', label: 'Rejected'   },
                    processing: { bg: '#eff6ff', color: '#1d4ed8', label: 'Processing' },
                  }
                  const st = ST[r.status] || ST.pending
                  return (
                    <div key={r.request_id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', padding: '12px 18px', borderBottom: i < payRequests.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                      <div style={{ fontSize: 11, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(r.requested_at)}</div>
                      <div style={{ fontSize: 10, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
                        {r.period_from ? `${fmt(r.period_from)} – ${fmt(r.period_to)}` : '—'}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                        {r.amount ? money(r.amount) : '—'}
                      </div>
                      <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.momo_number || '—'}
                      </div>
                      <div>
                        <span style={{ display: 'inline-block', background: st.bg, color: st.color, fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>
                          {st.label}
                        </span>
                        {r.admin_note && (
                          <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2, fontStyle: 'italic' }}>{r.admin_note}</div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Request Payout Modal ── */}
      {showPayModal && (
        <>
          <div onClick={() => setShowPayModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 500 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 16, zIndex: 501, boxShadow: '0 24px 80px rgba(0,0,0,0.18)', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>💸 Request Payout</div>
              <div onClick={() => setShowPayModal(false)} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 9, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>Pending Payout (Unsettled)</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#b45309', fontFamily: "'JetBrains Mono',monospace" }}>{money(unsettledTotal)}</div>
                <div style={{ fontSize: 10, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
                  {fmt(dateFrom)} – {fmt(dateTo)} · {orders.filter(o => !o.settled_batch_id).length} orders not yet disbursed
                </div>
                {settledTotal > 0 && (
                  <div style={{ fontSize: 10, color: '#15803d', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>
                    ✓ {money(settledTotal)} already paid out this period
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 5 }}>MoMo / Bank Number</label>
                <input value={payMomo} onChange={e => setPayMomo(e.target.value)} placeholder="078XXXXXXX — number to receive payment"
                  style={{ height: 42, borderRadius: 8, border: '1px solid #e9e9e4', padding: '0 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 5 }}>Note (optional)</label>
                <textarea value={payNote} onChange={e => setPayNote(e.target.value)} placeholder="Any special instructions…" rows={2}
                  style={{ borderRadius: 8, border: '1px solid #e9e9e4', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
              </div>
              {payError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {payError}</div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowPayModal(false)}
                  style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Cancel
                </button>
                <button onClick={handleRequestPayout} disabled={paySubmit}
                  style={{ flex: 2, height: 42, borderRadius: 8, border: 'none', background: paySubmit ? '#bbf7d0' : '#15803d', color: '#fff', fontSize: 13, fontWeight: 700, cursor: paySubmit ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {paySubmit ? 'Submitting…' : '✓ Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  )
}